import { useState, useRef, useEffect } from 'react';
import { useDeepSignal } from '../context';
import { INBOX_RAIL_MIN, INBOX_RAIL_MAX, INBOX_RAIL_DEFAULT, inboxModelColors } from '../constants';
import { insightCards } from '../fixtures';
import { buildInsightCardsFromApi } from '../builders';
import { getModelLanes } from '../model-lanes';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';

type InboxLayout = 'split' | 'accordion';
type SortBy = 'default' | 'type' | 'model';

export function WhatAiSaysSection() {
  const { apiData, loading } = useDeepSignal();

  const [layout, setLayout]           = useState<InboxLayout>('split');
  const [selectedId, setSelectedId]   = useState<number>(1);
  const [modelFilter, setModelFilter] = useState('All Models');
  const [sortBy, setSortBy]           = useState<SortBy>('default');
  const [railWidth, setRailWidth]     = useState(INBOX_RAIL_DEFAULT);
  const draggingRef  = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Drag-to-resize rail ──
  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const next = Math.min(INBOX_RAIL_MAX, Math.max(INBOX_RAIL_MIN, e.clientX - rect.left));
      setRailWidth(next);
    }
    function onUp() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3,4,5].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData) return <DSEmptyState message="No AI response data available for this run." />;

  const cards = buildInsightCardsFromApi(apiData);
  if (!cards.length) return <DSEmptyState message="No scored evidence quotes found in this run." />;

  // Data-driven model filter chips: only models that actually appear in this
  // run's evidence cards, ordered by the catalog. A model that wasn't run
  // (e.g. Gemini for Iterate) never shows up here.
  const cardModels = new Set<string>(cards.flatMap(c => c.models));
  const pills = ['All Models', ...getModelLanes(Object.keys(apiData.model_breakdown))
    .map(l => l.label)
    .filter(l => cardModels.has(l))];
  for (const m of cardModels) if (!pills.includes(m)) pills.push(m);

  // ── Filter + sort ──
  const filtered = modelFilter === 'All Models'
    ? cards
    : cards.filter(c => c.models.includes(modelFilter));

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'type')  return a.type.localeCompare(b.type);
    if (sortBy === 'model') return a.models[0].localeCompare(b.models[0]);
    return 0;
  });

  // Keep selection valid when filter changes
  const selectedCard = sorted.find(c => c.id === selectedId) ?? sorted[0];

  // ── Row component ──
  function InboxRow({ card, isSelected }: { card: typeof insightCards[0]; isSelected: boolean }) {
    return (
      <div
        onClick={() => setSelectedId(card.id)}
        style={{
          display: 'flex',
          cursor: 'pointer',
          borderBottom: '1px solid var(--border)',
          backgroundColor: isSelected ? 'var(--background)' : 'transparent',
          boxShadow: isSelected ? 'var(--elevation-sm)' : 'none',
        }}
      >
        {/* Accent bar */}
        <div style={{ width: '3px', flexShrink: 0, backgroundColor: card.accentColor }} />

        <div style={{ padding: '18px 16px', flex: 1, minWidth: 0 }}>
          {/* Badges row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <span style={{
              backgroundColor: card.typeBg,
              color: card.typeColor,
              borderRadius: '4px',
              padding: '2px 7px',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.03em',
              flexShrink: 0,
            }}>
              {card.type}
            </span>
            {card.models.map(m => {
              const mc = inboxModelColors[m] ?? { color: '#667085', bg: '#f2f4f7', border: '#e4e7ec' };
              return (
                <span key={m} style={{
                  backgroundColor: mc.bg,
                  color: mc.color,
                  border: `1px solid ${mc.border}`,
                  borderRadius: '4px',
                  padding: '1px 6px',
                  fontSize: '10px',
                  fontWeight: 500,
                  flexShrink: 0,
                }}>
                  {m}
                </span>
              );
            })}
          </div>

          {/* Headline */}
          <p style={{
            fontSize: '12px',
            fontWeight: isSelected ? 600 : 500,
            color: isSelected ? 'var(--foreground)' : '#414651',
            lineHeight: '1.45',
            margin: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {card.headline}
          </p>

          {/* Snippet */}
          <p style={{
            fontSize: '11px',
            color: 'var(--muted-foreground)',
            lineHeight: '1.4',
            margin: '6px 0 0',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {card.snippet}
          </p>
        </div>
      </div>
    );
  }

  // ── Detail panel ──
  function DetailPanel({ card }: { card: typeof insightCards[0] }) {
    return (
      <div style={{ padding: '28px 32px', overflowY: 'auto', height: '100%' }}>
        {/* Badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{
            backgroundColor: card.typeBg,
            color: card.typeColor,
            borderRadius: '4px',
            padding: '3px 9px',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.03em',
          }}>
            {card.type}
          </span>
          {card.models.map(m => {
            const mc = inboxModelColors[m] ?? { color: '#667085', bg: '#f2f4f7', border: '#e4e7ec' };
            return (
              <span key={m} style={{
                backgroundColor: mc.bg,
                color: mc.color,
                border: `1px solid ${mc.border}`,
                borderRadius: '4px',
                padding: '3px 9px',
                fontSize: '11px',
                fontWeight: 500,
              }}>
                {m}
              </span>
            );
          })}
        </div>

        {/* Headline */}
        <p style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 600,
          color: 'var(--foreground)',
          lineHeight: '1.45',
          marginBottom: '20px',
        }}>
          {card.headline}
        </p>

        {/* Full model response */}
        <div style={{
          borderLeft: `3px solid ${card.accentColor}`,
          paddingLeft: '16px',
          marginBottom: '20px',
        }}>
          <p style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            letterSpacing: '0.5px',
            color: 'var(--muted-foreground)',
            marginBottom: '8px',
          }}>
            MODEL RESPONSE
          </p>
          {card.fullQuote.split('\n\n').map((para, i) => (
            <p key={i} style={{
              fontSize: 'var(--text-sm)',
              color: i === 0 ? '#414651' : 'var(--muted-foreground)',
              lineHeight: '1.65',
              fontStyle: i === 0 ? 'italic' : 'normal',
              marginBottom: i < card.fullQuote.split('\n\n').length - 1 ? '12px' : '0',
            }}>
              {para}
            </p>
          ))}
        </div>

        {/* Source */}
        <div style={{
          backgroundColor: 'var(--muted)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '10px 14px',
        }}>
          <p style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            letterSpacing: '0.5px',
            color: 'var(--muted-foreground)',
            marginBottom: '2px',
          }}>
            SOURCE QUERY
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', margin: 0 }}>
            {card.source}
          </p>
        </div>
      </div>
    );
  }

  // ── Accordion row (mobile / accordion mode) ──
  const [expandedAccordion, setExpandedAccordion] = useState<Set<number>>(new Set());
  const toggleAccordion = (id: number) => setExpandedAccordion(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Header ── */}
      <div style={{ padding: '24px 32px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <SectionLabel>What AI Says</SectionLabel>
            <SectionTitle size="xl">Interesting patterns and signals from raw model responses.</SectionTitle>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginTop: '4px' }}>
              Select a signal to read the full model response.
            </p>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, paddingTop: '4px' }}>
            {/* Sort */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortBy)}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-xs)',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <option value="default">Sort: Default</option>
              <option value="type">Sort: Signal type</option>
              <option value="model">Sort: Model</option>
            </select>

            {/* Layout toggle */}
            <div style={{
              display: 'flex',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              overflow: 'hidden',
            }}>
              {(['split', 'accordion'] as InboxLayout[]).map(l => (
                <button
                  key={l}
                  onClick={() => setLayout(l)}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRight: l === 'split' ? '1px solid var(--border)' : 'none',
                    backgroundColor: layout === l ? 'var(--muted)' : 'var(--background)',
                    color: layout === l ? 'var(--foreground)' : 'var(--muted-foreground)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: layout === l ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  {l === 'split' ? 'Split' : 'List'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Model filter pills */}
        <div style={{ display: 'flex', gap: '6px', marginTop: '14px', flexWrap: 'wrap' }}>
          {pills.map(p => (
            <button
              key={p}
              onClick={() => { setModelFilter(p); if (sorted.length > 0) setSelectedId(sorted[0].id); }}
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                border: modelFilter === p ? '1px solid rgba(0,201,196,0.35)' : '1px solid var(--border)',
                backgroundColor: modelFilter === p ? '#e1fdfd' : 'var(--background)',
                color: modelFilter === p ? '#00c9c4' : 'var(--muted-foreground)',
                fontSize: 'var(--text-xs)',
                fontWeight: modelFilter === p ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      {layout === 'split' ? (
        <div ref={containerRef} style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* Left rail */}
          <div style={{ width: railWidth, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid var(--border)', backgroundColor: 'var(--muted)' }}>
            {sorted.length === 0 ? (
              <p style={{ padding: '20px 16px', fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
                No signals for this filter.
              </p>
            ) : (
              sorted.map(card => (
                <InboxRow key={card.id} card={card} isSelected={selectedCard?.id === card.id} />
              ))
            )}
          </div>

          {/* Drag handle */}
          <div
            onMouseDown={startDrag}
            style={{
              width: '4px',
              flexShrink: 0,
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--border)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          />

          {/* Right detail */}
          <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
            {selectedCard ? (
              <DetailPanel card={selectedCard} />
            ) : (
              <div style={{ padding: '40px 32px', color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
                Select a signal from the list.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Accordion mode */
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {sorted.length === 0 ? (
            <p style={{ padding: '20px 32px', fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              No signals for this filter.
            </p>
          ) : (
            sorted.map(card => {
              const isOpen = expandedAccordion.has(card.id);
              return (
                <div key={card.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* Accordion header */}
                  <div
                    onClick={() => toggleAccordion(card.id)}
                    style={{ display: 'flex', cursor: 'pointer', backgroundColor: isOpen ? 'var(--muted)' : 'transparent' }}
                  >
                    <div style={{ width: '3px', flexShrink: 0, backgroundColor: card.accentColor }} />
                    <div style={{ padding: '14px 32px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <span style={{ backgroundColor: card.typeBg, color: card.typeColor, borderRadius: '4px', padding: '2px 7px', fontSize: '10px', fontWeight: 600 }}>
                            {card.type}
                          </span>
                          {card.models.map(m => {
                            const mc = inboxModelColors[m] ?? { color: '#667085', bg: '#f2f4f7', border: '#e4e7ec' };
                            return (
                              <span key={m} style={{ backgroundColor: mc.bg, color: mc.color, border: `1px solid ${mc.border}`, borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: 500 }}>
                                {m}
                              </span>
                            );
                          })}
                        </div>
                        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)', margin: 0 }}>
                          {card.headline}
                        </p>
                      </div>
                      <span style={{ color: 'var(--muted-foreground)', fontSize: '16px', flexShrink: 0 }}>
                        {isOpen ? '↑' : '↓'}
                      </span>
                    </div>
                  </div>
                  {/* Accordion body */}
                  {isOpen && (
                    <div style={{ borderTop: '1px solid var(--border)' }}>
                      <DetailPanel card={card} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
